import { clone, delay } from "./client";
import { api, USE_MOCK_API } from "./http";
import {
  CATEGORY_FILTERS,
  CATEGORY_TABS,
  COLLECTIONS,
  COLOR_FILTERS,
  NEW_ARRIVAL_SLUGS,
  PRODUCTS,
  PROMO_CODES,
  SIZE_FILTERS,
  SORT_OPTIONS,
} from "./mock-data";
import type {
  CategoryFilter,
  CategoryTab,
  Collection,
  Product,
  ProductListResult,
  ProductQuery,
  PromoValidation,
  StockRow,
} from "./types";

function subSlug(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** GET /api/catalog/products */
export async function getProducts(query: ProductQuery = {}): Promise<ProductListResult> {
  const { category, sub, q, sort = "popularity", page = 1, perPage = 8 } = query;

  if (!USE_MOCK_API) {
    return api.get<ProductListResult>("/catalog/products", {
      category,
      sub: sub === "all" ? undefined : sub,
      q,
      sort,
      page,
      perPage,
    });
  }

  const needle = (q ?? "").trim().toLowerCase();
  let items = PRODUCTS.filter(
    (p) =>
      p.status !== "archived" &&
      (!category || category === "all" || p.categorySlug === category) &&
      (!sub || sub === "all" || subSlug(p.category) === sub) &&
      (!needle || p.name.toLowerCase().includes(needle) || p.ref.toLowerCase().includes(needle)),
  );

  items = [...items].sort((a, b) => {
    if (sort === "price-asc") return a.price - b.price;
    if (sort === "price-desc") return b.price - a.price;
    if (sort === "newest") return b.createdAt.localeCompare(a.createdAt);
    return b.popularity - a.popularity;
  });

  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * perPage;

  return delay(
    clone({
      items: items.slice(start, start + perPage),
      total,
      page: safePage,
      totalPages,
    }),
  );
}

/** GET /api/catalog/products/:slug */
export async function getProduct(slug: string): Promise<Product | null> {
  if (!USE_MOCK_API) {
    try {
      return await api.get<Product>(`/catalog/products/${encodeURIComponent(slug)}`);
    } catch {
      return null;
    }
  }
  const product = PRODUCTS.find((p) => p.slug === slug && p.status !== "archived") ?? null;
  return delay(product ? clone(product) : null);
}

/** GET /api/catalog/products/:slug/buy-it-with */
export async function getBuyItWith(slug: string): Promise<Product[]> {
  if (!USE_MOCK_API) {
    try {
      return await api.get<Product[]>(
        `/catalog/products/${encodeURIComponent(slug)}/buy-it-with`,
      );
    } catch {
      return [];
    }
  }
  const items = PRODUCTS.filter((p) => p.slug !== slug && p.status !== "archived").slice(0, 4);
  return delay(clone(items), 160);
}

/** GET /api/catalog/new-arrivals */
export async function getNewArrivals(): Promise<Product[]> {
  if (!USE_MOCK_API) return api.get<Product[]>("/catalog/new-arrivals");

  const items = NEW_ARRIVAL_SLUGS.map((slug) => PRODUCTS.find((p) => p.slug === slug)).filter(
    (p): p is Product => Boolean(p) && p.status !== "archived",
  );
  return delay(clone(items));
}

/** GET /api/catalog/collections */
export async function getCollections(): Promise<Collection[]> {
  if (!USE_MOCK_API) return api.get<Collection[]>("/catalog/collections");
  return delay(clone(COLLECTIONS));
}

/** GET /api/catalog/categories */
export async function getCategoryTabs(): Promise<CategoryTab[]> {
  if (!USE_MOCK_API) return api.get<CategoryTab[]>("/catalog/categories");
  return delay(clone(CATEGORY_TABS), 120);
}

/** GET /api/catalog/categories/:slug/subcategories */
export async function getSubcategories(categorySlug: string): Promise<CategoryTab[]> {
  if (!USE_MOCK_API) {
    return api.get<CategoryTab[]>(
      `/catalog/categories/${encodeURIComponent(categorySlug)}/subcategories`,
    );
  }

  const seen = new Map<string, string>();
  for (const product of PRODUCTS) {
    if (product.categorySlug !== categorySlug) continue;
    const slug = subSlug(product.category);
    if (!seen.has(slug)) seen.set(slug, product.category);
  }
  return delay(
    Array.from(seen, ([slug, label]) => ({ slug, label })),
    120,
  );
}

export interface FilterFacets {
  categories: CategoryFilter[];
  sizes: string[];
  colors: { name: string; hex: string }[];
  sorts: { value: string; label: string }[];
}

/** GET /api/catalog/facets */
export async function getFilterFacets(): Promise<FilterFacets> {
  if (!USE_MOCK_API) return api.get<FilterFacets>("/catalog/facets");

  return delay(
    clone({
      categories: CATEGORY_FILTERS,
      sizes: SIZE_FILTERS,
      colors: COLOR_FILTERS,
      sorts: SORT_OPTIONS,
    }),
    120,
  );
}

/** GET /api/catalog/stock?ids=a,b,c — batch one call per page, never per card. */
export async function getStock(productIds: string[]): Promise<StockRow[]> {
  const ids = productIds.filter(Boolean);
  if (!ids.length) return [];
  if (!USE_MOCK_API) {
    return api.get<StockRow[]>("/catalog/stock", { ids: ids.join(",") });
  }
  const rows: StockRow[] = [];
  for (const product of PRODUCTS.filter((p) => ids.includes(p.id))) {
    for (const color of product.colors) {
      for (const size of product.sizes) rows.push({ color: color.name, size, qty: 12 });
    }
  }
  return delay(rows, 140);
}

/** GET /api/catalog/size-guides */
export async function getSizeGuides(): Promise<unknown[]> {
  if (!USE_MOCK_API) return api.get<unknown[]>("/catalog/size-guides");
  return delay([], 120);
}

/** POST /api/catalog/promo/validate — read-only discount preview. */
export async function validatePromo(code: string): Promise<PromoValidation> {
  const normalized = code.trim().toUpperCase();
  if (!USE_MOCK_API) return api.post<PromoValidation>("/catalog/promo/validate", { code: normalized });

  const rate = PROMO_CODES[normalized];
  return delay(
    rate
      ? { valid: true, code: normalized, percentOff: rate * 100, amountOff: 0 }
      : { valid: false, code: normalized, discount: 0 },
    220,
  );
}
